import React from "react";
import { motion } from "framer-motion";
import {
  Sparkle,
  PencilSimpleLine,
  PaperPlaneTilt,
  ClockCounterClockwise,
  Archive,
  Funnel,
  CheckSquare,
  ShieldCheck,
} from "@phosphor-icons/react";

const NEW_ITEMS = [
  {
    icon: PencilSimpleLine,
    title: "Anwendungen bearbeiten",
    text: "Nachträglich Korrekturen? Kein Problem, jede Anwendung öffnen, ändern, speichern. Alle Änderungen werden im Verlauf lückenlos protokolliert.",
  },
  {
    icon: ClockCounterClockwise,
    title: "Änderungsverlauf",
    text: "Wer hat wann was geändert? Der Audit-Trail zeigt es sekundengenau, Behördenfähig nach EU 2023/564.",
  },
  {
    icon: PaperPlaneTilt,
    title: "PDF per E-Mail",
    text: "Berater, Lohnunternehmer oder Kontrolleur brauchen Doku? Ein Klick, PDF ist per E-Mail unterwegs, mit Nachrichtentext.",
  },
  {
    icon: CheckSquare,
    title: "Sammel-Export",
    text: "Mehrere Anwendungen auf einmal auswählen und als PDF oder JSON runterladen. Perfekt für Quartals- oder Saisonberichte.",
  },
  {
    icon: Funnel,
    title: "Intelligente Filter",
    text: "Nach Fläche, Kultur, Verwendungsart oder Datum filtern. Genau die Aufzeichnungen finden, die du gerade brauchst.",
  },
  {
    icon: Archive,
    title: "Backup als ZIP",
    text: "Alle Daten deines Betriebs als ZIP herunterladen: JSON + PDF + Änderungsverlauf. DSGVO-konform, kein Vendor-Lock-in.",
  },
  {
    icon: Sparkle,
    title: "KI liest Pachtverträge",
    text: "Foto vom Pachtvertrag oder Bescheid, Plantus erkennt Größe, Flurstück, Eigentümer automatisch. In der Mobile-App.",
  },
  {
    icon: ShieldCheck,
    title: "12 BVL-Verwendungsarten",
    text: "Von Agrarfläche bis Nichtkulturland-Gleisanlagen, alle amtlichen Kategorien für lückenlose Compliance.",
  },
];

export const NewFeatures = () => {
  return (
    <section
      id="was-neu"
      data-testid="new-features-section"
      className="relative py-24 sm:py-28 bg-gradient-to-b from-forest-900 via-forest-900 to-[#0f2a1e] text-white overflow-hidden scroll-mt-24"
    >
      {/* Subtle grain / dots */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-lime-400/15 border border-lime-400/30 text-lime-300 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] mb-5">
            <Sparkle size={14} weight="fill" /> Neu im Februar
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1]">
            <span className="text-lime-300">8 neue Funktionen</span> für
            noch schnellere Doku
          </h2>
          <p className="mt-5 font-body text-base sm:text-lg text-white/70 leading-relaxed">
            Plantus wächst mit deinen Anforderungen. Dieses Update bringt
            zurück, was Landwirte am meisten gefordert haben, und mehr.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {NEW_ITEMS.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05, ease: "easeOut" }}
              data-testid={`new-feature-${i}`}
              className="group relative rounded-2xl bg-white/[0.04] backdrop-blur-sm border border-white/10 hover:border-lime-400/30 hover:bg-white/[0.07] p-6 transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-xl bg-lime-400/15 border border-lime-400/25 flex items-center justify-center mb-4 group-hover:bg-lime-400/25 transition">
                <f.icon size={22} weight="duotone" className="text-lime-300" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white leading-snug">
                {f.title}
              </h3>
              <p className="mt-2 font-body text-sm text-white/60 leading-relaxed">
                {f.text}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-14 flex flex-wrap items-center justify-between gap-6 rounded-2xl bg-lime-400/10 border border-lime-400/25 p-6 sm:p-7"
        >
          <div>
            <div className="font-heading text-xl font-semibold text-white">
              Alle Features sofort verfügbar
            </div>
            <p className="mt-1 font-body text-sm text-white/70">
              Kein Update nötig, die neuen Funktionen sind bereits Teil deines
              Plantus-Abos.
            </p>
          </div>
          <a
            href="/login"
            data-testid="new-features-cta"
            className="inline-flex items-center gap-2 bg-lime-400 hover:bg-lime-300 text-forest-900 font-semibold px-5 py-3 rounded-xl transition"
          >
            Jetzt einloggen und ausprobieren
          </a>
        </motion.div>
      </div>
    </section>
  );
};
